import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider } from "react-hook-form";
import * as yup from "yup";
import { useSelector } from "react-redux";

import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import BaseSwitch from "@/components_v2/FormFields/Switch/BaseSwitch";
import BaseFormFieldsList from "@/components_v2/FormFieldsList/BaseFormFieldsList";
import BaseStepper from "@/components_v2/Stepper/BaseStepper";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { useLazyGetUsersQuery } from "@/redux/service";
import {
  HookFormTextArea,
  HookFormBaseDropdown,
  HookFormInfiniteScrollCustomChoiceDropdown,
  HookFormDatePicker,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { BaseDatePickerHandle } from "@/components_v2/FormFields/DatePicker/types";
import { OrganizationRoles } from "@/shared/utils/constants";
import BaseButton from "@/components_v2/Button/BaseButton";
import { useEnabledEvidenceTemplates } from "@/domains/evidence/hooks/useEvidenceTemplates";
import { FullUserResponse } from "@/redux/users/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { Option } from "@/components_v2/FormFields/Dropdown/types";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import {
  DataBlockRequestFormItem,
  DataBlockRequestFormProps,
} from "@/domains/evidence-requests/types";
import { getRolesByPermissionLevel } from "@/domains/evidence-requests/permissions";

import "../styles.css";

const RequestForm = ({
  formHook,
  allowedTemplateIds,
}: DataBlockRequestFormProps) => {
  const { t } = useTranslation();
  const activeTemplates = useEnabledEvidenceTemplates(allowedTemplateIds);
  const [selectedUsers, setSelectedUsers] = useState<
    Record<string, Record<string, string> | undefined>
  >({});
  const [getUsers] = useLazyGetUsersQuery();
  const selectedUsersRef = useRef({});
  const datePickerRef = useRef<BaseDatePickerHandle>(null);
  const currentUser = useSelector(selectCurrentUser);

  const {
    form,
    currentStep,
    showIndividualMessage,
    handleShowIndividualMessageChange,
  } = formHook;

  useEffect(() => {
    selectedUsersRef.current = selectedUsers;
    const values = form.getValues();

    (values?.requests || []).forEach(
      (request: DataBlockRequestFormItem, index: number) => {
        const email = request.email as string;
        const user = selectedUsers[email];
        const fields = ["firstName", "lastName", "role"] as const;

        fields.forEach((field) => {
          form.setValue(`requests.${index}.${field}`, user ? user[field] : "");
        });

        form.clearErrors(fields.map((field) => `requests.${index}.${field}`));
      },
    );
  }, [selectedUsers]);

  const isExistingUserSelected = useCallback(
    (index: number) => {
      const values = form.getValues();
      const selectedItem = (values?.requests || [])[index];
      return selectedItem && selectedItem.email in selectedUsersRef.current;
    },
    [form],
  );

  const handleChangeSelectedUsers = (
    option: Option | (FullUserResponse & Option),
  ) => {
    if ("id" in option) {
      const user = option as FullUserResponse;
      setSelectedUsers((prevValue) => ({
        ...prevValue,
        [option.value]: {
          firstName: user.firstName,
          lastName: user.lastName,
          role: OrganizationRoles.getLabel(user.organizationRole),
        },
      }));
    } else {
      setSelectedUsers((prevValue) => {
        const newValue = { ...prevValue };
        delete newValue[option.value];
        return newValue;
      });
    }
  };

  const handleEndOfDayClick = () => {
    // Work around to set the current date manually since otherwise the previous value will overwrite it
    if (datePickerRef.current) {
      datePickerRef.current.close();
    }
    setTimeout(() => {
      form.setValue("expiresAt", DateTime.now().endOf("day"));
    }, 50);
  };

  const fields = useMemo(() => {
    return [
      ...(currentUser
        ? [
            {
              name: "email",
              required: true,
              label: t("datablocks.labels.email"),
              component: HookFormInfiniteScrollCustomChoiceDropdown,
              props: {
                name: "email",
                className: "data-block-request-form-email-dropdown",
                control: form.control,
                fetchFn: getUsers,
                labelFieldName: "fullName",
                valueFieldName: "email",
                searchFieldName: "search",
                defaultFilters: {
                  organizations: currentUser.organization?.id,
                  exclude: currentUser.id,
                },
                placeholder: t("datablocks.placeholders.email") as string,
                onChangeCallback: handleChangeSelectedUsers,
                customChoiceOptions: {
                  schema: yup
                    .string()
                    .email(t("datablocks.errors.emailNotValid"))
                    .required(t("form.validation.itemRequired"))
                    .typeError(t("form.validation.itemNotValid")),
                  placeholder: t(
                    "datablocks.labels.enterEmailAddress",
                  ) as string,
                },
                "aria-autocomplete": "none" as const,
              },
            },
            {
              name: "firstName",
              required: false,
              label: t("datablocks.labels.firstName"),
              component: HookFormInput,
              props: {
                control: form.control,
                placeholder: t("datablocks.placeholders.firstName") as string,
              },
              disabled: isExistingUserSelected,
            },
            {
              name: "lastName",
              required: false,
              label: t("datablocks.labels.lastName"),
              component: HookFormInput,
              props: {
                control: form.control,
                placeholder: t("datablocks.placeholders.lastName") as string,
              },
              disabled: isExistingUserSelected,
            },
            {
              name: "role",
              required: true,
              label: t("datablocks.labels.role"),
              component: HookFormBaseDropdown,
              props: {
                control: form.control,
                options: getRolesByPermissionLevel(
                  currentUser?.organizationRole,
                ),
                placeholder: t("datablocks.placeholders.role") as string,
                "aria-autocomplete": "none" as const,
              },
              disabled: isExistingUserSelected,
            },
          ]
        : []),
      ...(showIndividualMessage
        ? [
            {
              name: "message",
              required: false,
              label: t("datablocks.labels.message"),
              component: HookFormTextArea,
              props: {
                control: form.control,
                placeholder: t("datablocks.placeholders.message") as string,
              },
            },
          ]
        : []),
    ];
  }, [currentUser, form, showIndividualMessage]);

  const defaultRequestMessage = useMemo(() => {
    if (!currentUser) return null;
    return t("datablocks.labels.dataBlockRequestDefaultMessage", {
      organizationName:
        currentUser.organization?.name || t("datablocks.labels.ourInstitution"),
    });
  }, [currentUser]);

  useEffect(() => {
    if (defaultRequestMessage && !form.getValues().message) {
      form.setValue("message", defaultRequestMessage);
    }
  }, [currentStep, defaultRequestMessage]);

  const RequestSteps = useMemo(() => {
    if (!activeTemplates) return [];

    return [
      <div className="data-block-request-form-details-step">
        <FormFieldWrapper
          label={t("datablocks.labels.dataTemplateType")}
          description={
            allowedTemplateIds
              ? t("datablocks.labels.evidenceTypeHelpTextWithAllowedTypes")
              : t("datablocks.labels.evidenceTypeHelpText")
          }
          error={form.formState.errors.dataTemplate}
          required
        >
          <HookFormBaseDropdown
            name="dataTemplate"
            control={form.control}
            options={activeTemplates.map((template) => ({
              label: template.title,
              value: template.id,
            }))}
            placeholder={t("datablocks.placeholders.selectTemplate")}
          />
        </FormFieldWrapper>
        <FormFieldWrapper
          label={t("datablocks.labels.expiryDate")}
          error={form.formState.errors.expiresAt}
          required
        >
          <HookFormDatePicker
            ref={datePickerRef}
            name="expiresAt"
            control={form.control}
            disabledDate={(current) =>
              current && current < DateTime.now().startOf("day")
            }
            showNow={false}
            renderExtraFooter={() => (
              <BaseButton
                variant={BUTTON_VARIANTS.LINK}
                isFullWidth
                onClick={handleEndOfDayClick}
              >
                {t("datablocks.labels.endOfDay")}
              </BaseButton>
            )}
          />
        </FormFieldWrapper>
      </div>,
      <div className="data-block-request-form-emails-step">
        <div className="data-block-request-form-emails-step-switch">
          <BaseSwitch
            value={showIndividualMessage}
            onChange={handleShowIndividualMessageChange}
          />
          <h6 className="body-s-regular">
            {t("datablocks.labels.customizeInviteMessages")}
          </h6>
        </div>

        <BaseFormFieldsList
          className="data-block-request-form-fields-list"
          title={t("datablocks.labels.emails")}
          description={t("datablocks.labels.invitesDescription")}
          name="requests"
          control={form.control}
          fields={fields}
          addButtonText={t("datablocks.labels.addNewUser")}
          headerTitle={t("datablocks.labels.user")}
          defaultValues={{ message: defaultRequestMessage }}
        />
        {!showIndividualMessage && (
          <HookFormTextArea
            name="message"
            placeholder={t("datablocks.placeholders.message")}
            control={form.control}
          />
        )}
      </div>,
    ];
  }, [activeTemplates, defaultRequestMessage, showIndividualMessage]);

  return (
    <FormProvider {...form}>
      <div className="data-block-request-form">
        <BaseStepper
          current={currentStep}
          items={[
            {
              title: t("datablocks.labels.inviteDetails"),
            },
            {
              title: t("datablocks.labels.inviteEmails"),
            },
          ]}
        />
        {!activeTemplates && <BaseSpinner />}
        {RequestSteps[currentStep]}
      </div>
    </FormProvider>
  );
};

export default RequestForm;
